import {
  addPublishFileTypes,
  ArticlePreview,
  ArticleType,
  editArticleTypes,
  editMetadataTypes,
  putKeywordsTypes,
  searchByDateTypes,
  SearchPayload,
  SuccessType,
  trendArticlesType,
  updateKeywordsTypes,
} from "./articlesTypes";
import {
  createApiClient,
  handleGetRequest,
  handlePostRequest,
} from "../apiService";
import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { suggestedReviewerTypes } from "../authors/authorTypes";
import { useToken } from "../tokenUtils";
import {
  matchesNormalizedSearchInFields,
  normalizeSearchTerm,
  sanitizeSearchInput,
} from "../../utils/textUtils";

const apiClient = createApiClient("/");
const domain = getSubdomain();

const getArticles = async (): Promise<ArticleType[]> => {
  const currentToken = useToken();

  const res = await apiClient.get(`${domain}/articles`, {
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });

  return res.data as ArticleType[];
};

const getArticlesAmountForStatus = async (): Promise<
  Record<string, number>
> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("amount token not found.");
  }
  const res = await apiClient.get(`${domain}/articles_amount_for_status`, {
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });
  return res.data as Record<string, number>;
};

const getLatestArticlesByVolume = async (): Promise<ArticlePreview[]> => {
  const currentToken = useToken();
  return await handleGetRequest<ArticlePreview[]>(
    apiClient,
    `${domain}/articles-latest`,
    currentToken || undefined,
  );
};

const getArticlesByVolume = async (
  volume_slug: string,
): Promise<ArticlePreview[]> => {
  return await handleGetRequest<ArticlePreview[]>(
    apiClient,
    `${domain}/volumes/${volume_slug}/articles`,
  );
};

const getRecentArticles = async (): Promise<trendArticlesType[]> => {
  // const currentToken = useToken();
  // return await handleGetRequest<trendArticlesType[]>(apiClient, `${domain}/articles-recent`, currentToken || undefined);
  return await handleGetRequest<trendArticlesType[]>(
    apiClient,
    `${domain}/articles-recent`,
  );
};

const getMostDownloadedArticles = async (): Promise<trendArticlesType[]> => {
  // const currentToken = useToken();
  // if (!currentToken) {
  //   throw new Error("downloads token not found.");
  // }
  //return await handleGetRequest<trendArticlesType[]>(apiClient, `${domain}/articles-downloads`, currentToken || undefined);
  return await handleGetRequest<trendArticlesType[]>(
    apiClient,
    `${domain}/articles-downloads`,
  );
};

const getMostViewedArticles = async (): Promise<trendArticlesType[]> => {
  // const currentToken = useToken();
  // if (!currentToken) {
  //   throw new Error("views token not found.");
  // }
  // return await handleGetRequest<trendArticlesType[]>(apiClient, `${domain}/articles-views`, currentToken || undefined);
  return await handleGetRequest<trendArticlesType[]>(
    apiClient,
    `${domain}/articles-views`,
  );
};

const getArticleBySlug = async (slug: string): Promise<ArticleType> => {
  const currentToken = useToken();

  if (!slug) {
    throw new Error("Slug parameter is required");
  }
  const response = await handleGetRequest(
    apiClient,
    `${domain}/articles/${slug}`,
    currentToken,
  );
  return response as ArticleType;
};

const getPublishedArticleBySlug = async (
  slug: string,
): Promise<ArticleType> => {
  const response = await apiClient.get(`${domain}/articles/${slug}/published`);
  return response.data as ArticleType;
};

const getArticleById = async (id: number): Promise<ArticleType> => {
  const currentToken = useToken();

  return await handleGetRequest<ArticleType>(
    apiClient,
    `/articles/id/${id}`,
    currentToken,
  );
};

const registerDownload = async (article_slug: string): Promise<SuccessType> => {
  const currentToken = useToken();

  return await handlePostRequest<SuccessType>(
    apiClient,
    `${domain}/articles/${article_slug}/download/`,
    {},
    currentToken,
  );
};

const registerDownloadWithoutLogin = async (
  article_slug: string,
): Promise<SuccessType> => {
  return await handlePostRequest<SuccessType>(
    apiClient,
    `${domain}/articles/${article_slug}/download-no-login/`,
    {},
    null,
  );
};

const searchEndpoint = `${domain}/articles_search/search`;

const getSearchSource = (article: trendArticlesType): ArticleType => {
  return article._source;
};

const matchesIgnoringDiacritics = (
  article: trendArticlesType,
  normalizedQuery: string,
): boolean => {
  if (!normalizedQuery) return true;

  const source = getSearchSource(article);
  const keywordStrings = [
    ...(source.keywords_language_1 || []),
    ...(source.keywords_language_2 || []),
    ...(source.keywords_language_3 || []),
  ];

  return matchesNormalizedSearchInFields(normalizedQuery, [
    source.title_language_1,
    source.title_language_2,
    source.title_language_3,
    source.abstract_language_1,
    source.abstract_language_2,
    source.abstract_language_3,
    source.article_rich_text,
    source.authors_flat_text_short,
    source.authors_flat_text_rich,
    source.citation_text,
    source.doi,
    ...keywordStrings,
  ]);
};

const mergeBySlug = (
  primary: trendArticlesType[],
  extra: trendArticlesType[],
): trendArticlesType[] => {
  const merged: trendArticlesType[] = [];
  const seen = new Set<string>();

  const appendUnique = (items: trendArticlesType[]) => {
    items.forEach((item) => {
      const slug = getSearchSource(item).slug;
      if (!slug || seen.has(slug)) return;
      seen.add(slug);
      merged.push(item);
    });
  };

  appendUnique(primary);
  appendUnique(extra);
  return merged;
};

const requestSearch = async (
  query_text: string,
): Promise<trendArticlesType[]> => {
  const response = await apiClient.get<trendArticlesType[]>(searchEndpoint, {
    params: { query_text },
  });
  return response.data || [];
};

const searchQuery = async (
  query_text: string,
): Promise<trendArticlesType[]> => {
  const normalizedQuery = normalizeSearchTerm(query_text);
  const primaryResults = await requestSearch(sanitizeSearchInput(query_text));

  if (!normalizedQuery) {
    return primaryResults;
  }

  const alreadyHasAccentInsensitiveMatch = primaryResults.some((article) =>
    matchesIgnoringDiacritics(article, normalizedQuery),
  );

  if (alreadyHasAccentInsensitiveMatch) {
    return primaryResults;
  }

  const fallbackQueries = ["", "*"];

  for (const fallbackQuery of fallbackQueries) {
    try {
      const fallbackResults = await requestSearch(fallbackQuery);
      if (!fallbackResults.length) continue;

      const filteredFallbackResults = fallbackResults.filter((article) =>
        matchesIgnoringDiacritics(article, normalizedQuery),
      );

      if (filteredFallbackResults.length > 0) {
        return mergeBySlug(primaryResults, filteredFallbackResults);
      }
    } catch {
      // Keep primary results if fallback endpoint variants are not supported.
    }
  }

  return primaryResults;
};

const editArticles = async (articleEditRequest: editArticleTypes) => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("Token de autenticación faltante");
  }
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };
  const res = await apiClient.patch(
    `${domain}/author/articles/${articleEditRequest.article_slug}/author_edit`,
    articleEditRequest.data,
    { headers },
  );
  return res.data;
};

const getArticleSuggestedReviewers = async (
  slug: string,
): Promise<suggestedReviewerTypes[]> => {
  const currentToken = useToken();

  const suggestedReviewers = await handleGetRequest<suggestedReviewerTypes[]>(
    apiClient,
    `${domain}/author/articles/${slug}/suggest_reviewer`,
    currentToken,
  );
  return suggestedReviewers;
};

const addPublishFile = async (
  addPublishFileRequest: addPublishFileTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();

  const combinedFormData = new FormData();
  const fileValue = addPublishFileRequest.file.get("file");
  if (fileValue) {
    combinedFormData.append("file", fileValue as Blob | string);
  } else {
    throw new Error('No file found in FormData under key "file".');
  }

  combinedFormData.append("body", JSON.stringify(addPublishFileRequest.body));
  const headers = {
    Authorization: `Bearer ${currentToken}`,
  };

  const path = `${domain}/articles/${addPublishFileRequest.body.article_slug}/publish_file?language=${addPublishFileRequest.body.language}&save_first_page_as_cover=${addPublishFileRequest.body.save_first_page_as_cover}`;
  const res = await apiClient.put(path, combinedFormData, { headers });
  return res.data as SuccessType;
};

const addPublishXMLFile = async (
  addPublishFileRequest: addPublishFileTypes,
): Promise<SuccessType> => {
  const currentToken = useToken();
  const fileFormData = new FormData();

  const fileValue = addPublishFileRequest.file.get("file");
  if (fileValue) {
    // Solo enviamos el archivo puro y duro, como espera FastAPI
    fileFormData.append("file", fileValue as Blob | string);
  } else {
    throw new Error('No file found in FormData under key "file".');
  }

  const headers = {
    Authorization: `Bearer ${currentToken}`,
  };

  const artSlug = addPublishFileRequest.body.article_slug;

  // Ruta exacta que coincide con tu @router.put del backend
  const path = `${domain}/articles/${artSlug}/publish_xml_file`;

  const res = await apiClient.put(path, fileFormData, { headers });
  return res.data as SuccessType;
};

const addArticleCover = async (data: {
  article_slug: string;
  file: FormData;
}): Promise<string> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("No se encontró token de autenticación.");
  }

  const headers = {
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.put(
    `${domain}/articles/${data.article_slug}/cover_file`,
    data.file,
    { headers },
  );
  return res.data as string;
};

const cleanHTML = (html: string) => {
  if (typeof html !== "string") return html;
  return html
    .replace(/\\"/g, '"')
    .replace(/\\n/g, "")
    .replace(/\\t/g, "")
    .trim();
};

const editMetadata = async (
  editMetadataRequest: editMetadataTypes,
): Promise<any> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("Authentication token not found.");
  }

  const cleanedData = Object.fromEntries(
    Object.entries(editMetadataRequest.data).map(([key, value]) => {
      if (typeof value === "string" && value.includes("<")) {
        return [key, cleanHTML(value)];
      }
      return [key, value];
    }),
  );

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.patch(
    `${domain}/articles/${editMetadataRequest.article_slug}/edit`,
    cleanedData,
    { headers },
  );

  return res.data;
};

const searchByDate = async (
  searchByDateRequest: searchByDateTypes,
): Promise<SearchPayload> => {
  const start_date = searchByDateRequest.start_date;
  const end_date = searchByDateRequest.end_date;
  const response = await apiClient.get<SearchPayload>(
    `${domain}/articles_search/by_date`,
    {
      params: {
        start_date,
        end_date,
      },
    },
  );
  return response.data;
};

const putKeywords = async (
  putKeywordsRequest: putKeywordsTypes,
): Promise<updateKeywordsTypes> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("Authentication token not found.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.put(
    `${domain}/article-keywords/${putKeywordsRequest.article_slug}`,
    putKeywordsRequest.keywords,
    { headers },
  );
  return res.data as updateKeywordsTypes;
};

const getArticlesPreviewByVolume = async (
  volume_slug: string,
): Promise<ArticlePreview[]> => {
  const response = await apiClient.get<ArticlePreview[]>(
    `${domain}/articles-by-volumes/${volume_slug}/articles`,
  );
  return response.data;
};

const getLatestArticlesPreview = async (): Promise<ArticlePreview[]> => {
  const currentToken = useToken();
  return await handleGetRequest<ArticlePreview[]>(
    apiClient,
    `${domain}/articles-latest-preview`,
    currentToken || undefined,
  );
};

const getArticlesMetrics = async (): Promise<ArticleType[]> => {
  const currentToken = useToken();

  const res = await apiClient.get(`${domain}/articles-metrics`, {
    headers: {
      Authorization: `Bearer ${currentToken}`,
    },
  });

  return res.data as ArticleType[];
};

const generateDoi = async ({ article_slug, sufix }): Promise<any> => {
  const currentToken = useToken();
  if (!currentToken) {
    throw new Error("Authentication token not found.");
  }

  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${currentToken}`,
  };

  const res = await apiClient.post(
    `${domain}/metadata/${article_slug}/doi?article_suffix=${sufix}`,
    {},
    { headers },
  );

  return res.data;
};

const articlesService = {
  getArticles,
  getArticlesAmountForStatus,
  getArticleBySlug,
  getArticleById,
  registerDownload,
  registerDownloadWithoutLogin,
  getLatestArticlesByVolume,
  getArticlesByVolume,
  getRecentArticles,
  getMostDownloadedArticles,
  getMostViewedArticles,
  searchQuery,
  editArticles,
  getArticleSuggestedReviewers,
  getPublishedArticleBySlug,
  addPublishFile,
  addPublishXMLFile,
  editMetadata,
  putKeywords,
  searchByDate,
  getArticlesPreviewByVolume,
  getLatestArticlesPreview,
  getArticlesMetrics,
  generateDoi,
  addArticleCover,
};

export default articlesService;
